import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, Info, Activity, FileText, ChevronDown, AlertCircle, Beaker, Pill, Shield, Check } from 'lucide-react';
import { LL37Diagram } from '../../assets/diagrams/LL37Diagram';
import KeyTakeaway from '@/components/ui/key-takeaway';

export default function LL37Content() {
  return (
    <ScrollArea className="h-[calc(100vh-16rem)] pr-4">
      <div className="mb-6 flex gap-6 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <h2 className="text-2xl font-bold mb-4 text-blue-800">LL-37 (Cathelicidin) Overview</h2>
          <p className="mb-4">
            LL-37 is a human host-defense peptide with dual antimicrobial action and immunomodulatory functions. As the only 
            human cathelicidin, this 37-amino acid peptide plays a critical role in innate immunity and wound healing.
          </p>
          <p className="mb-4">
            Its primary mechanisms include directly disrupting bacterial membranes and biofilms, helping to eliminate pathogens 
            that may be resistant to conventional antibiotics. Uniquely, LL-37 also promotes tissue repair and wound healing by 
            activating formyl peptide receptors (e.g., FPR2/FPRL1) on cells, leading to enhanced cell migration and re-epithelialization.
          </p>
          <p>
            LL-37 further influences inflammation by binding bacterial lipopolysaccharides (LPS), preventing excessive cytokine release 
            during infections, while also inducing angiogenesis (new blood vessel formation) critical for tissue repair.
          </p>
        </div>
        <div className="flex-1 min-w-[400px] flex items-center justify-center">
          <div className="w-[90%]">
            <LL37Diagram />
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4 text-blue-700">Key Mechanisms and Cross-talk with Other Pathways</h3>
        
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm mb-6">
          <table className="w-full border-collapse bg-white text-sm">
            <thead>
              <tr>
                <th className="border-b border-gray-200 bg-gray-50 p-4 text-left font-medium text-gray-700">Activity</th>
                <th className="border-b border-gray-200 bg-gray-50 p-4 text-left font-medium text-gray-700">Mechanism</th>
                <th className="border-b border-gray-200 bg-gray-50 p-4 text-left font-medium text-gray-700">Clinical Relevance</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border-b border-gray-200 p-4 font-medium text-red-600">Antimicrobial</td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Direct membranolytic action on bacteria; disrupts bacterial membrane integrity
                </td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Effective against both Gram+ and Gram- bacteria, even antibiotic-resistant strains
                </td>
              </tr>
              <tr>
                <td className="border-b border-gray-200 p-4 font-medium text-orange-600">Anti-biofilm</td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Prevents bacterial attachment; alters bacterial gene expression and quorum sensing
                </td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Overcomes biofilm-associated resistance in chronic infections and wounds
                </td>
              </tr>
              <tr>
                <td className="border-b border-gray-200 p-4 font-medium text-green-600">Wound Healing</td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Activates keratinocyte migration via FPR2/ILK signaling; stimulates re-epithelialization
                </td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Accelerates closure of chronic wounds and ulcers
                </td>
              </tr>
              <tr>
                <td className="border-b border-gray-200 p-4 font-medium text-blue-600">Angiogenesis</td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Upregulates HIF-1α and VEGF; acts via FPRL1 to induce new blood vessel formation
                </td>
                <td className="border-b border-gray-200 p-4 text-gray-700">
                  Improves blood supply to healing tissue, crucial for ischemic wounds
                </td>
              </tr>
              <tr>
                <td className="p-4 font-medium text-purple-600">Immunomodulation</td>
                <td className="p-4 text-gray-700">
                  Binds LPS to prevent excessive TNF-α/IL-1 release; context-dependent inflammation control
                </td>
                <td className="p-4 text-gray-700">
                  Helps orchestrate balanced immune response without excessive inflammation
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-r-lg mb-6">
          <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
            <Info className="h-5 w-5 mr-2 text-blue-600" />
            Cross-talk with Other Pathways
          </h4>
          <p className="text-blue-900">
            LL-37 sits at the intersection of antimicrobial defense and tissue regeneration. It interacts with immune 
            receptors like FPR2 on neutrophils, macrophages, and lymphocytes to modulate inflammation. It also interferes 
            with TLR signaling: by sequestering LPS and even self-DNA/RNA, LL-37 can alter TLR4 and TLR9 activation, 
            balancing pro- and anti-inflammatory signals. In wound healing, LL-37's promotion of angiogenesis involves 
            cross-talk with growth factor pathways, mimicking some effects of bFGF/VEGF. Additionally, vitamin D–VDR 
            signaling cross-talks with LL-37 expression, with sufficient vitamin D required for optimal cathelicidin levels.
          </p>
        </div>
      </div>
      
      <KeyTakeaway>
        LL-37 is unique among peptides for its dual antimicrobial and wound-healing properties. Its ability to directly 
        kill bacteria (including resistant strains) while simultaneously promoting tissue repair makes it especially 
        valuable for infected chronic wounds. The peptide bridges innate immunity and regenerative pathways, 
        functioning at the intersection of infection control and tissue healing.
      </KeyTakeaway>

      <div className="mt-8 mb-8">
        <h3 className="text-xl font-semibold mb-4 text-blue-700">Clinical Applications and Dosing</h3>
        
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl shadow-md p-6 mb-6">
          <h4 className="text-lg font-semibold text-indigo-800 mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2 text-indigo-600" />
            Dosing Protocols
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h5 className="font-medium text-indigo-700 mb-2">Topical Application</h5>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">1</span>
                  <div>
                    <span className="font-semibold">Concentration:</span> 0.5–1.6 mg/mL in gel formulation
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">2</span>
                  <div>
                    <span className="font-semibold">Frequency:</span> Applied daily to wound bed
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">3</span>
                  <div>
                    <span className="font-semibold">Duration:</span> 4-8 weeks, depending on wound response
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">4</span>
                  <div>
                    <span className="font-semibold">Caution:</span> Higher concentrations (&gt;3.2 mg/mL) may cause irritation
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h5 className="font-medium text-purple-700 mb-2">Local Injection (Investigational)</h5>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">1</span>
                  <div>
                    <span className="font-semibold">Dose:</span> 50–100 μg per injection site
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">2</span>
                  <div>
                    <span className="font-semibold">Technique:</span> Intradermal or perilesional around chronic wounds
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">3</span>
                  <div>
                    <span className="font-semibold">Frequency:</span> 1–3× weekly, guided by wound response
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">4</span>
                  <div>
                    <span className="font-semibold">Note:</span> Research-use only; not routine clinical practice
                  </div>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-4 bg-white rounded-lg p-4 shadow-sm">
            <h5 className="font-medium text-blue-700 mb-2">Endogenous Optimization Approach</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">1</span>
                  <div>
                    <span className="font-semibold">Method:</span> Vitamin D supplementation to enhance endogenous LL-37
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">2</span>
                  <div>
                    <span className="font-semibold">Mechanism:</span> 1,25(OH)₂D stimulates cathelicidin gene expression
                  </div>
                </li>
              </ul>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">3</span>
                  <div>
                    <span className="font-semibold">Monitoring:</span> Check 25(OH)D levels in patients with chronic wounds
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center mt-0.5 mr-2">4</span>
                  <div>
                    <span className="font-semibold">Approach:</span> Target vitamin D sufficiency (&gt;30 ng/mL) to enhance host defense
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <details className="bg-gray-50 rounded-lg mb-6 overflow-hidden transition-all">
          <summary className="bg-gray-100 px-6 py-3 cursor-pointer font-medium text-gray-700 flex items-center justify-between hover:bg-gray-200 transition-colors">
            <span>Storage and Handling Considerations</span>
            <ChevronDown className="h-5 w-5 text-gray-500" />
          </summary>
          <div className="p-6">
            <ul className="list-disc pl-5 mb-4 space-y-1 text-gray-700">
              <li>LL-37 is susceptible to proteolytic degradation in wound environments</li>
              <li>Incorporation into hydrogels or controlled-release formulations increases stability</li>
              <li>For topical preparations, refrigerate when not in use</li>
              <li>For research formulations, lyophilized peptide should be stored at -20°C</li>
              <li>Avoid repeated freeze-thaw cycles to maintain bioactivity</li>
            </ul>
          </div>
        </details>
      </div>

      <div className="mt-6 mb-8">
        <h3 className="text-xl font-semibold mb-4 text-blue-700">Clinical Benefits and Synergistic Combinations</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 transition-all hover:shadow-lg">
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3">
              <h4 className="text-white font-medium">Key Therapeutic Benefits</h4>
            </div>
            <div className="p-4">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">Enhanced Wound Healing:</span> Accelerates chronic wound closure, particularly in diabetic and venous ulcers</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">Biofilm Disruption:</span> Breaks down bacterial biofilms and inhibits their formation</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">Antimicrobial Action:</span> Direct killing of pathogens including resistant strains</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">Angiogenesis Promotion:</span> Stimulates new blood vessel growth in poorly vascularized tissue</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">Balanced Immune Response:</span> Modulates inflammation to control infection without excessive tissue damage</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 transition-all hover:shadow-lg">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3">
              <h4 className="text-white font-medium">Synergistic Combinations</h4>
            </div>
            <div className="p-4">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">With Antibiotics:</span> LL-37 breaks down biofilms, increasing bacterial susceptibility to conventional antibiotics</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">With Growth Factors:</span> Combining with PDGF or bFGF creates complementary wound-healing effects</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">With Vitamin D3:</span> Vitamin D supplementation enhances endogenous LL-37 production</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">With Thymosin α-1:</span> LL-37 provides immediate antimicrobial action while Thymosin α-1 enhances adaptive immunity</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700"><span className="font-medium">With Advanced Wound Therapies:</span> Enhances effectiveness of negative-pressure wound therapy or hyperbaric oxygen</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
          <h4 className="font-semibold text-amber-800 mb-2 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2 text-amber-600" />
            Important Cautions and Potential Risks
          </h4>
          <ul className="space-y-2 text-amber-900">
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span>Autoimmune concern: Use with caution in active autoimmune skin diseases (psoriasis, lupus)</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span>Local reactions: High concentrations (&gt;3.2 mg/mL) may cause local inflammation and irritation</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span>Regulatory status: LL-37 is investigational; not FDA-approved for clinical use</span>
            </li>
            <li className="flex items-start">
              <span className="text-amber-700 mr-2">•</span>
              <span>Peptide instability: Rapid degradation in protease-rich wound environments may reduce efficacy</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-6 mb-8">
        <h3 className="text-xl font-semibold mb-4 text-blue-700">Monitoring and Clinical Guidance</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3">
              <h4 className="text-white font-medium">Relevant Biomarkers</h4>
            </div>
            <div className="p-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Biomarker</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clinical Significance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-700">Wound Size Reduction</td>
                    <td className="px-4 py-2 text-sm text-gray-700">Primary indicator of therapeutic success</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-700">C-Reactive Protein (CRP)</td>
                    <td className="px-4 py-2 text-sm text-gray-700">Decreasing levels suggest reduced infection/inflammation</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-700">Wound Swab Culture</td>
                    <td className="px-4 py-2 text-sm text-gray-700">Shift from pathogens to normal flora indicates antimicrobial effect</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-700">Cytokine Levels (research)</td>
                    <td className="px-4 py-2 text-sm text-gray-700">Reduction in TNF-α, IL-1β, IL-6 in wound exudate</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-700">Periwound Blood Flow</td>
                    <td className="px-4 py-2 text-sm text-gray-700">Increased flow indicates pro-angiogenic effect</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 mb-6">
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-4 py-3">
                <h4 className="text-white font-medium">Selection Guidance vs. Other Peptides</h4>
              </div>
              <div className="p-4">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="flex-shrink-0">
                      <Pill className="h-5 w-5 text-teal-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-700">Choose LL-37 when infection control AND tissue regeneration are both needed</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0">
                      <Pill className="h-5 w-5 text-teal-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-700">For clean but non-healing wounds without infection, thymosin β4 or BPC-157 may be more appropriate</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0">
                      <Pill className="h-5 w-5 text-teal-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-700">LL-37 is superior to defensin peptides when angiogenesis is needed in ischemic wounds</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0">
                      <Pill className="h-5 w-5 text-teal-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-700">Consider alternative approaches in patients with autoimmune tendencies</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Beaker className="h-5 w-5 text-blue-500" />
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800">Clinical Pearls</h4>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Rapid biofilm clearance may initially increase wound inflammation before improvement</li>
                      <li>Always check vitamin D status in patients with chronic infections or ulcers</li>
                      <li>LL-37 is particularly useful in areas with poor circulation (diabetic limbs, pressure ulcer sites)</li>
                      <li>Improvement in wound appearance (granulation) often precedes reduction in wound size</li>
                      <li>Consider perilesional micro-injections (50μg) to stimulate edge vascularization in stalled wounds</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mb-8 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">References</h3>
        <ul className="space-y-2 text-sm text-gray-700">
          <li>Duplantier AJ, van Hoek ML (2013) – Review of LL-37's therapeutic potential in polymicrobial wound infections, discussing its anti-biofilm, antimicrobial, and immunomodulatory effects.</li>
          <li>Gallo RL et al. (2004) – Nature study demonstrating LL-37 induces angiogenesis via FPRL1, linking inflammation and neovascularization in wounds.</li>
          <li>Clinical Trials Arena (2013) – Phase I/II trial news: Topical LL-37 (0.5–3.2 mg/mL) in leg ulcers improved healing, with best results at 0.5–1.6 mg/mL and mild reactions at highest dose.</li>
          <li>Chitosan Hydrogel Study (2020) – Found LL-37 in a hydrogel enhanced pressure ulcer healing in mice, via reduced TNF-α and increased VEGF/HIF-1α in tissue.</li>
          <li>Tørholm Christensen et al. (2023) – Arch Dermatol Res: LL-37 cream in diabetic foot ulcers improved healing rate (clinical trial).</li>
        </ul>
      </div>
    </ScrollArea>
  );
}